import { Site } from '@envato/foundation-types'
import * as classnames from 'classnames'
import * as React from 'react'

import { config } from '../../../common/config'
import { IItem } from '../../../typings/searchResults'
import get from 'lodash-es/get'
import head from 'lodash-es/head'
import Sanitizer from '../../../common/utils/sanitizer'
import EmptyResultsPlaceholder from '../../components/emptyResultsPlaceholder'
import Helmet from 'react-helmet'
import HorizontalMenu from '../../components/horizontalMenu'
import ItemsGrid from '../../components/itemsGrid'
import LoadingSpinnerPlaceholder from '../../components/loadingSpinnerPlaceholder'
import PageContentSection from '../../components/pageContentSection'
import SearchPageContentWrapper from '../../components/searchPageContentWrapper'
import PageHeader from '../../components/pageHeader'
import { LinkTab, Tabs } from '../../components/tabs'
import ThumbnailsSidebar from '../../components/thumbnailsSidebar'
import UsDollarsNoticeContainer from '../../components/usDollarsNotice/usDollarsNoticeContainer'
import VideoControlsContainer from '../../components/video/videoControlsContainer'

import * as styles from './topSellersPage.module.css'

import { Props } from '../../containers/topSellersPageContainer'

const makeBreadcrumbLink = ({ category }: { category: string }) => {
  return '/popular_item/by_category?category=' + category
}

// Also used in client/components/imagePreview/imagePreview.tsx
// Worth investigating whether to move the fetching of image
// to its own function
const landscapePreviewUrl = (previews: IItem['previews']) => {
  const urls =
    get(previews, 'landscape_preview.image_urls') ||
    get(previews, 'icon_with_video_preview.image_urls') ||
    get(previews, 'icon_with_landscape_preview.image_urls') ||
    []

  return get(head(urls), 'url')
}

const squarePreviewUrl = (previews: IItem['previews']) => {
  const urls =
    get(previews, 'icon_with_square_preview.image_urls') ||
    get(previews, 'landscape_preview.image_urls') ||
    get(previews, 'icon_with_landscape_preview.image_urls') ||
    []

  return get(head(urls), 'url')
}

const getMarketingImg = (previews: IItem['previews']): any =>
  get(previews, 'landscape_preview.landscape_url') ||
  get(previews, 'icon_with_video_preview.landscape_url') ||
  get(previews, 'icon_with_landscape_preview.landscape_url') ||
  get(previews, 'icon_with_square_preview.square_url')

const getImagePreview = (previews: IItem['previews']): any =>
  squarePreviewUrl(previews) ||
  landscapePreviewUrl(previews) ||
  getMarketingImg(previews)

const itemSchemas = (items: IItem[]) => {
  return [
    {
      type: 'application/ld+json',
      innerHTML: `{
      "@context": "http://schema.org",
      "@type": "ItemList",
      "itemListElement": [
        ${items.map((item, index) => {
          return `{
            "@type": "ListItem",
            "position": ${index + 1},
            "name": "${item.name}",
            "url": "${item.url}",
            "image": "${getImagePreview(item.previews)}"
          }`
        })}
      ]
    }`
    }
  ]
}

const TopSellersPage = (props: Props) => {
  const {
    items,
    featuredAuthors,
    location,
    experimentsReady,
    currentSite,
    isLoading,
    metadata,
    subcategories,
    currentCategoryName
  } = props
  const hasItems = items && items.length > 0

  const category = new Sanitizer(location.query.category)
    .length({ max: 100 })
    // eslint-disable-next-line
    .replace(/[^\w\/-]/gi)
    .asString()

  const period = new Sanitizer(location.query.period, 'week').asValidString([
    'week',
    'month'
  ])

  const defaultAvatarImgSrc = `${
    config.assets.PUBLIC_PATH
  }/images/default-user.jpg`
  const featuredAuthorsThumbnails = featuredAuthors.map(author => ({
    imgSrc: author.profile_image_url || defaultAvatarImgSrc,
    link: author.profile_path,
    title: author.username
  }))
  const shouldShowVideoControls = currentSite === Site.videohive

  const tabsValue = `?period=${period}`
  const title = metadata ? metadata.heading : 'Top Sellers'

  const className = classnames({
    [styles.withCategory]: !!currentCategoryName,
    [styles.withSubcategories]: !!subcategories
  })

  const renderItemListSchema = hasItems && currentSite === Site.themeforest

  return (
    <>
      {renderItemListSchema && <Helmet script={itemSchemas(items)} />}
      <article className={className}>
        <PageHeader
          heading={title}
          category={category}
          makeBreadcrumbLink={makeBreadcrumbLink}
          subHeading={metadata ? metadata.sub_heading : ''}
          currentSite={currentSite}
          defaultBreadcrumbs={[
            { name: 'Home', path: '/' },
            { name: 'Best Sellers' }
          ]}
        />
        <SearchPageContentWrapper
          experimentsReady={experimentsReady}
          currentSite={currentSite}
        >
          <PageContentSection withShadow>
            <div className={styles.navbar}>
              {currentCategoryName && (
                <div className={styles.categoryName}>
                  More Best Selling: <b>{currentCategoryName}:</b>
                </div>
              )}
              {subcategories && (
                <HorizontalMenu>
                  {subcategories.map(catg => (
                    <HorizontalMenu.MenuItem
                      to={`?category=${catg.path}`}
                      key={catg.path}
                    >
                      {catg.name}
                    </HorizontalMenu.MenuItem>
                  ))}
                </HorizontalMenu>
              )}
              {!subcategories && !category && (
                <Tabs value={tabsValue}>
                  <LinkTab to="?period=week">This week</LinkTab>
                  <LinkTab to="?period=month">Last month</LinkTab>
                </Tabs>
              )}
              <div className={styles.controls}>
                <UsDollarsNoticeContainer />
                {shouldShowVideoControls && <VideoControlsContainer />}
              </div>
            </div>
            {isLoading && <LoadingSpinnerPlaceholder />}
            {hasItems && (
              <div className={styles.itemsGrid}>
                <ItemsGrid
                  items={items}
                  location={location}
                  listName="Top Sellers: Top Selling Items"
                  showSingleRow={false}
                />
                <div className={styles.sidebar}>
                  <ThumbnailsSidebar
                    title="Featured Authors"
                    thumbnails={featuredAuthorsThumbnails}
                  />
                </div>
              </div>
            )}
            {!isLoading && !hasItems && (
              <EmptyResultsPlaceholder
                heading="Nothing exists here"
                subHeading="We couldn't find any data for this page. Please try again later."
              />
            )}
          </PageContentSection>
        </SearchPageContentWrapper>
      </article>
    </>
  )
}

export default TopSellersPage
